import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Alert, Snackbar } from "@mui/material";

const createToastBus = () => {
	const listeners = new Set();

	return {
		subscribe: (listener) => {
			listeners.add(listener);
			return () => listeners.delete(listener);
		},
		trigger: (config = {}) => {
			const payload = typeof config === "string" ? { message: config } : config;
			listeners.forEach((listener) => listener(payload || {}));
		},
	};
};

export const toast = createToastBus();

const ToastContext = createContext({
	trigger: () => { },
	close: () => { },
});

const DEFAULT_TOAST = {
	open: false,
	message: "",
	severity: "info",
	autoHideDuration: 4000,
	action: null,
};

export const ToastProvider = ({ children }) => {
	const [currentToast, setCurrentToast] = useState(DEFAULT_TOAST);

	useEffect(() => {
		const unsubscribe = toast.subscribe((config = {}) => {
			if (!config?.message) {
				return;
			}

			setCurrentToast({
				open: true,
				message: config.message,
				severity: config.severity || "info",
				autoHideDuration: config.autoHideDuration ?? DEFAULT_TOAST.autoHideDuration,
				action: config.action || null,
				key: Date.now(),
			});
		});

		return unsubscribe;
	}, []);

	const handleClose = useCallback((_, reason) => {
		if (reason === "clickaway") return;
		setCurrentToast((prev) => ({ ...prev, open: false }));
	}, []);

	const contextValue = useMemo(
		() => ({
			trigger: (config) => toast.trigger(config),
			close: handleClose,
		}),
		[handleClose]
	);

	return (
		<ToastContext.Provider value={contextValue}>
			{children}
			<Snackbar
				open={currentToast.open}
				autoHideDuration={currentToast.autoHideDuration}
				onClose={handleClose}
				anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
				key={currentToast.key}
			>
				<Alert
					onClose={handleClose}
					severity={currentToast.severity}
					sx={{ width: "100%" }}
					action={currentToast.action}
					variant="filled"
				>
					{currentToast.message}
				</Alert>
			</Snackbar>
		</ToastContext.Provider>
	);
};

export const useToast = () => useContext(ToastContext);
